import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { setAccessToken, setUser } from 'redux/slices/appSlice';
import { useAppDispatch, useAppSelector } from 'redux/store';

/**
 * Custom hook for authentication and permission management
 * @returns Authentication state including loading status, access token, and permissions
 */
export const useAuthentication = (): {
  isLoading: boolean;
  accessToken: string | null;
  permissions: string[] | null;
} => {
  const { accessToken } = useAppSelector((state) => state.app);
  const dispatch = useAppDispatch();
  const { isAuthenticated, loginWithRedirect, isLoading, user, getAccessTokenSilently } = useAuth0();

  // Use refs to track previous values and prevent unnecessary updates
  const prevTokenRef = useRef<string | null>(null);
  const prevUserRef = useRef(user);

  const [permissions, setPermissions] = useState<string[] | null>(null);
  const [tokenError, setTokenError] = useState<Error | null>(null);

  // Memoize the redirect configuration
  const redirectConfig = useMemo(
    () => ({
      appState: { targetUrl: '/' }
    }),
    []
  );

  // Memoize permission extraction function
  const extractPermissions = useCallback((token: string) => {
    try {
      const payload = JSON.parse(window.atob(token.split('.')[1]));
      return payload.permissions.filter((permission: string) => {
        const prefix = permission.split(':')[0];
        return prefix === 'useApplication' || prefix === 'useApplciation';
      });
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return [];
    }
  }, []);

  // Get access token silently and handle errors
  const getAndSetAccessToken = useCallback(async () => {
    if (isAuthenticated && accessToken === null) {
      try {
        const token = await getAccessTokenSilently();
        dispatch(setAccessToken(token));
      } catch (error) {
        console.error('Error getting access token:', error);
        loginWithRedirect(redirectConfig);
      }
    }
  }, [accessToken, dispatch, getAccessTokenSilently, isAuthenticated, loginWithRedirect, redirectConfig]);

  // Check if User is logged in
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      loginWithRedirect(redirectConfig);
    } else {
      getAndSetAccessToken();
    }
  }, [isAuthenticated, isLoading, loginWithRedirect, redirectConfig, getAndSetAccessToken]);

  // Handle user and permission updates
  useEffect(() => {
    let shouldUpdate = false;

    // Only update user if it has changed
    if (user && user !== prevUserRef.current) {
      prevUserRef.current = user;
      dispatch(setUser(user));
    }

    // Only update permissions if token has changed
    if (accessToken && accessToken !== prevTokenRef.current) {
      prevTokenRef.current = accessToken;
      const newPermissions = extractPermissions(accessToken);

      // Only update permissions if they've actually changed
      if (JSON.stringify(permissions) !== JSON.stringify(newPermissions)) {
        setPermissions(newPermissions);
        shouldUpdate = true;
      }
    }

    // Batch updates if needed
    if (shouldUpdate) {
      setTokenError(null);
    }
  }, [dispatch, user, accessToken, extractPermissions, permissions]);

  // Return memoized values
  return useMemo(
    () => ({
      isLoading: isLoading || (isAuthenticated && accessToken === null),
      accessToken,
      permissions: permissions || [] // Always return an array to prevent null checks
    }),
    [isLoading, isAuthenticated, accessToken, permissions]
  );
};
